import type { BusinessInfo, MeetingMode } from "../lib/api.ts";
import { MeetingBadge } from "./MeetingBadge.tsx";

interface BusinessHeaderProps {
  business: BusinessInfo;
  meetingModes?: MeetingMode[];
}

export function BusinessHeader({ business, meetingModes }: BusinessHeaderProps) {
  const address = business.address;
  const addressStr = [address.line1, address.city, address.state, address.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="bg-white border-b border-gray-100">
      {/* Cover image or gradient fallback */}
      {business.cover_url ? (
        <div className="w-full h-36 sm:h-48 overflow-hidden">
          <img
            src={business.cover_url}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div
          className="w-full h-24 sm:h-32"
          style={{
            background: `linear-gradient(135deg, var(--accent) 0%, color-mix(in srgb, var(--accent) 60%, #000) 100%)`,
          }}
        />
      )}

      <div className="max-w-2xl mx-auto px-4 pb-6">
        {/* Logo / avatar — overlaps cover */}
        <div className="-mt-8 mb-4 flex items-end gap-4">
          {business.logo_url ? (
            <img
              src={business.logo_url}
              alt={`${business.name} logo`}
              className="w-16 h-16 rounded-[var(--radius)] object-cover shadow-md border-2 border-white bg-white"
            />
          ) : (
            <div
              className="w-16 h-16 rounded-[var(--radius)] flex items-center justify-center text-2xl font-bold text-white shadow-md border-2 border-white"
              style={{ backgroundColor: "var(--accent)" }}
            >
              {business.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Name + tagline */}
        <h1 className="text-2xl font-bold text-gray-900 leading-tight">{business.name}</h1>
        {business.tagline && (
          <p className="mt-1 text-gray-500 text-sm">{business.tagline}</p>
        )}

        {/* Meeting mode badges */}
        {meetingModes && meetingModes.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {[...new Set(meetingModes)].map((mode) => (
              <MeetingBadge key={mode} mode={mode} />
            ))}
          </div>
        )}

        {/* Contact info */}
        {(addressStr || business.phone || business.email || business.website_url) && (
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-gray-500">
            {addressStr && <span>{addressStr}</span>}
            {business.phone && (
              <a href={`tel:${business.phone}`} className="hover:text-gray-900 transition-colors">
                {business.phone}
              </a>
            )}
            {business.email && (
              <a href={`mailto:${business.email}`} className="hover:text-gray-900 transition-colors">
                {business.email}
              </a>
            )}
            {business.website_url && (
              <a
                href={business.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-900 transition-colors"
              >
                {business.website_url.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
